import React, { useEffect, useRef, useState } from "react";
import * as echarts from 'echarts';
import { useChartOptions } from '@sg/utils/useChartOptions';
import {debounce} from 'lodash-es';

const ChartEchats = (props: any)=> {
  const reactEchartsRef = useRef(null);
  const chartInstanceRef = useRef<any>(null);
  const legendSelectedRef = useRef<Record<string, boolean>>({});
  const [legendSelectionVersion, setLegendSelectionVersion] = useState(0);
  const {
    height = 343,
    options = '',
    type = 'line',
    numberType = '',
    formatType = 'million',
    setOtherConfigFunc,
    key,
    onChartReady,
    notMerge = true,
  } = props;
  const mergeOptions = useChartOptions({
    userOptions: options,
    type: type,
    excludeOptions: ['xAxis'],
    numberType,
    formatType,
  });
  const timer = useRef<any>(null);
  const idleTaskRef = useRef<number | null>(null);
  const updateSeqRef = useRef(0);
  const setOtherConfigFuncRef = useRef<any>(setOtherConfigFunc);
  useEffect(() => {
    setOtherConfigFuncRef.current = setOtherConfigFunc;
  }, [setOtherConfigFunc]);
  const applyLegendSelectedToOptions = (
    sourceOptions: any,
    selectedMap: Record<string, boolean>,
  ) => {
    if (!Object.keys(selectedMap || {}).length) {
      return sourceOptions;
    }
    const normalizedLegend = Array.isArray(sourceOptions?.legend)
      ? sourceOptions.legend
      : sourceOptions?.legend
        ? [sourceOptions.legend]
        : [];
    if (!normalizedLegend.length) {
      return sourceOptions;
    }
    return {
      ...sourceOptions,
      legend: normalizedLegend.map((legendItem: any) => ({
        ...legendItem,
        // Merge runtime legend state so helper-series logic can follow hide/show.
        selected: {
          ...(legendItem?.selected || {}),
          ...selectedMap,
        },
      })),
    };
  };

  // Initialize chart instance only once
  useEffect(() => {
    if (reactEchartsRef.current && !chartInstanceRef.current) {
      // Safety: dispose any existing chart instance on this DOM element
      const existingInstance = echarts.getInstanceByDom(reactEchartsRef.current);
      if (existingInstance) {
        existingInstance.dispose();
      }
      
      chartInstanceRef.current = echarts.init(reactEchartsRef.current, null, {
        // useDirtyRect is set to false because it created phantom lines on chart
        useDirtyRect: false // 仅重绘变化区域
      });

      // Call onChartReady callback if provided
      if (onChartReady && chartInstanceRef.current) {
        onChartReady(chartInstanceRef.current);
      }

      chartInstanceRef.current?.on('finished', () => {
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          // Chart rendering finished
          // Call onChartReady again after chart is fully rendered
          if (onChartReady && chartInstanceRef.current) {
            onChartReady(chartInstanceRef.current);
          }
        }, 200);
      });
      chartInstanceRef.current?.on('legendselectchanged', (event: any) => {
        legendSelectedRef.current = event?.selected || {};
        // Force option recompute so page utilities can react to legend visibility.
        setLegendSelectionVersion((prev) => prev + 1);
      });
    }

    // 添加 resize 事件监听器
    const handleResize = debounce(() => {
      chartInstanceRef.current?.resize();
    }, 200);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
      chartInstanceRef.current?.off('legendselectchanged');
      chartInstanceRef.current?.off('finished');
      if (idleTaskRef.current !== null && typeof (window as any).cancelIdleCallback === 'function') {
        (window as any).cancelIdleCallback(idleTaskRef.current);
        idleTaskRef.current = null;
      }
      // Dispose chart instance to clean up all event listeners and prevent memory leaks
      if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []); // Only run on mount/unmount

  // Update chart options when data changes
  useEffect(() => {
    const localChart = chartInstanceRef.current;
    if (!localChart || localChart.isDisposed()) return;
    const currentSeq = ++updateSeqRef.current;
    if (idleTaskRef.current !== null && typeof (window as any).cancelIdleCallback === 'function') {
      (window as any).cancelIdleCallback(idleTaskRef.current);
      idleTaskRef.current = null;
    }

    const updateOptions = () => {
      // Double-check chart is still valid before updating
      if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
        return;
      }
      // Skip stale async updates when rapidly switching view/project/date
      if (currentSeq !== updateSeqRef.current) {
        return;
      }
      
      const optionsWithLegendSelected = applyLegendSelectedToOptions(
        mergeOptions,
        legendSelectedRef.current,
      );
      const finalOptions = setOtherConfigFuncRef.current
        ? setOtherConfigFuncRef.current(optionsWithLegendSelected)
        : optionsWithLegendSelected;
      // Add confine to prevent tooltip from going outside chart container
      if (finalOptions.tooltip) {
        finalOptions.tooltip = {
          ...finalOptions.tooltip,
          confine: true,
        };
      }
      
      try {
        chartInstanceRef.current.setOption(finalOptions, {
          notMerge,
          lazyUpdate: false,
        });
      } catch (err) {
        // Catch any errors during setOption to prevent crashes
        console.error('Error in chart setOption:', err);
      }
    };

    try {
      if (typeof (window as any).requestIdleCallback === 'function') {
        idleTaskRef.current = (window as any).requestIdleCallback(updateOptions, { timeout: 1200 });
      } else {
        updateOptions();
      }
    } catch (error) {
      console.error('Error setting chart options:', error);
      updateOptions();
    }
    return () => {
      if (idleTaskRef.current !== null && typeof (window as any).cancelIdleCallback === 'function') {
        (window as any).cancelIdleCallback(idleTaskRef.current);
        idleTaskRef.current = null;
      }
    };
  }, [legendSelectionVersion, mergeOptions, notMerge]); // Update when options change

  return <div ref={reactEchartsRef} style={{ width: '100%', height }} key={key}></div>;
};
export default ChartEchats;